import { type ForwardedRef, forwardRef } from 'react';

import clsx from 'clsx';
import ReactSelect, {
	type CSSObjectWithLabel,
	type Props as ReactSelectProps,
	type SelectInstance,
	type StylesConfig,
	type Theme
} from 'react-select';

import type { BaseSelectOption } from '../form/select-control';
export interface InputSelectProps<
	OptionType extends BaseSelectOption,
	IsMulti extends boolean
> extends ReactSelectProps<OptionType, IsMulti> {
	isRequired?: boolean;
	error?: boolean;
}

export const SelectStyle = <
	OptionType extends BaseSelectOption,
	IsMulti extends boolean
>(
	error: boolean | undefined
): StylesConfig<OptionType, IsMulti> => ({
	control: (baseStyles: CSSObjectWithLabel, props) => ({
		...baseStyles,
		border: clsx('1px solid', error ? '#FF0000' : 'border-slate-200'),
		boxShadow: 'none',
		'&:hover': {
			border: clsx('1px solid', error ? '#FF0000' : 'border-slate-200')
		},
		borderRadius: '6px',
		maxHeight: 'fit',
		minHeight: '32px',
		backgroundColor: props.isDisabled ? '#e5e7eb' : 'transparent',
		fontSize: '14px',
		opacity: props.isDisabled ? 0.5 : baseStyles.opacity
	}),
	placeholder: (baseStyles: CSSObjectWithLabel) => ({
		...baseStyles,
		color: 'hsla(219, 13%, 74%, 1)',
		fontSize: '14px',
		fontWeight: 400
	}),

	multiValue: (baseStyles: CSSObjectWithLabel) => ({
		...baseStyles,
		backgroundColor: 'transparent'
	}),
	multiValueLabel: (baseStyles: CSSObjectWithLabel, props) => ({
		...baseStyles,
		backgroundColor: 'rgba(0, 0, 0, 0)',
		paddingInline: '10px',
		color: 'black',
		borderTopWidth: 1,
		borderLeftWidth: 1,
		borderBottomWidth: 1,
		borderRightWidth: props.isDisabled ? 1 : baseStyles.borderRightWidth,
		borderRadius: 0,
		borderColor: 'rgba(0, 0, 0, 0.2)',
		borderTopLeftRadius: '20px',
		borderBottomLeftRadius: '20px',
		borderTopRightRadius: props.data?.isFixed || props.isDisabled ? '20px' : 0,
		borderBottomRightRadius: props.data?.isFixed || props.isDisabled ? '20px' : 0
	}),
	multiValueRemove: (baseStyles: CSSObjectWithLabel, props) => ({
		...baseStyles,
		backgroundColor: 'rgba(0, 0, 0, 0)',
		color: '#000',
		borderTopWidth: 1,
		borderRightWidth: 1,
		borderBottomWidth: 1,
		borderRadius: 0,
		borderColor: 'rgba(0, 0, 0, 0.2)',
		borderTopRightRadius: '20px',
		borderBottomRightRadius: '20px',
		'&:hover': {
			backgroundColor: 'rgba(0, 0, 0, 0.2)',
			color: 'black'
		},
		display: props.isDisabled ? 'none' : baseStyles.display
	}),
	dropdownIndicator: (baseStyles: CSSObjectWithLabel) => ({
		...baseStyles,
		color: 'rgba(137, 147, 164, 1)'
	}),
	indicatorSeparator: (baseStyles: CSSObjectWithLabel) => ({
		...baseStyles,
		display: 'none'
	}),
	menuPortal: (baseStyles: CSSObjectWithLabel) => ({
		...baseStyles,
		zIndex: 51,
		pointerEvents: 'all'
	}),
	menu: (baseStyles: CSSObjectWithLabel) => ({
		...baseStyles,
		zIndex: 51
	}),
	groupHeading: (baseStyles) => ({
		...baseStyles,
		paddingLeft: '25px',
		fontWeight: 'bold',
		color: 'black'
	})
});

export const SelectTheme = (theme: Theme) => ({
	...theme,
	colors: {
		...theme.colors,
		primary25: 'rgba(15, 171, 255, 0.6)',
		primary75: 'rgba(15, 171, 255, 0.8)',
		primary: 'rgba(15, 171, 255)'
	}
});

function InputSelectInner<
	OptionType extends BaseSelectOption,
	IsMulti extends boolean
>(
	{ error, ...props }: InputSelectProps<OptionType, IsMulti>,
	ref: ForwardedRef<SelectInstance<OptionType, IsMulti>>
) {
	return (
		<ReactSelect<OptionType, IsMulti>
			styles={{ ...SelectStyle<OptionType, IsMulti>(error) }}
			theme={SelectTheme}
			components={{ ...props.components }}
			{...props}
			noOptionsMessage={() => 'Data tidak ditemukan'}
			ref={ref}
		/>
	);
}

export const InputSelect = forwardRef(InputSelectInner) as <
	OptionType extends BaseSelectOption,
	IsMulti extends boolean = false
>(
	props: InputSelectProps<OptionType, IsMulti> & {
		ref?: ForwardedRef<SelectInstance<OptionType, IsMulti>>
	}
) => React.ReactElement;
